import { useEffect, useMemo, useRef, useState } from 'react';
import { useMasterContext } from '../context';
import type { Products } from '../api/graphql/products/types';
import type { Subscription } from '../types/constant/SubscriptionType';
import type { ParentParams } from '../types/constant/PagesPropsType';
import type { PlanGroupsStateType } from '../types/api/PlanGroupApiType';
import type { SubscriptionProductViewItem } from '../utils/subscriptionDetail/types';
import { getPlanGroups } from '../utils/getPlanGroups';
import { fetchGraphqlProducts } from '../api/graphql/products';
import { getAllProductIds, getCategoriesProductIds } from '../utils/subscriptionDetail/productsIds';
import { satisfiedBoxSettings } from '../utils/boxSettings';
import { createCategoryContext } from '../utils/categories';
import { buildSubscriptionProducts } from '../utils/subscriptionDetail/subscriptionProducts';
import { mapListLineToApiLine } from '../utils/subscriptionList/listLineToApiLine';

/**
 * マイアカTOP用。詳細の「次回お届け」と同じ buildSubscriptionProducts から subscribedProducts だけ求める。
 * totalOrderCount は 0 固定、sellingPlanId は空（一覧 API に無いため）。サムネ並び専用。
 */
export function useSubscriptionListSubscribedDisplay(
  subscriptions: Subscription[],
  parentParams: ParentParams,
  token: string
) {
  const { categories, boxSettings } = useMasterContext();
  const [planGroups, setPlanGroups] = useState<PlanGroupsStateType>({});
  const hasFetchedPlanGroupsRef = useRef(false);
  const [products, setProducts] = useState<Products | null>(null);
  const lastProductIdsKeyRef = useRef('');

  const { customerTags, totalSpent } = parentParams;

  const targetSubs = useMemo(
    () =>
      subscriptions.filter(
        (s) =>
          (s.status === 'ACTIVE' || s.status === 'PAUSED') && s.subscriptionLines.length > 0
      ),
    [subscriptions]
  );

  useEffect(() => {
    if (!token) return;
    if (hasFetchedPlanGroupsRef.current) return;
    getPlanGroups(token).then((data) => {
      setPlanGroups(data);
      hasFetchedPlanGroupsRef.current = true;
    });
  }, [token]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!categories || !boxSettings) return;

      if (targetSubs.length === 0) {
        if (!cancelled) {
          setProducts({});
          lastProductIdsKeyRef.current = '';
        }
        return;
      }

      const union = new Set<string>();
      for (const sub of targetSubs) {
        const lines = sub.subscriptionLines.map(mapListLineToApiLine);
        getAllProductIds(lines, categories, boxSettings).forEach((id) => union.add(id));
      }
      const arr = Array.from(union);
      const key = arr.sort().join('\0');
      if (key !== '' && key === lastProductIdsKeyRef.current) return;

      if (arr.length === 0) {
        if (!cancelled) {
          setProducts({});
          lastProductIdsKeyRef.current = key;
        }
        return;
      }

      const res = await fetchGraphqlProducts(arr);
      if (!cancelled) {
        lastProductIdsKeyRef.current = key;
        setProducts(res);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [targetSubs, categories, boxSettings]);

  const subscribedByContractId = useMemo(() => {
    if (!categories || !boxSettings || products === null) return {};
    if (!hasFetchedPlanGroupsRef.current) return {};

    const categoryIds = getCategoriesProductIds(categories);
    const out: Record<string, SubscriptionProductViewItem[]> = {};

    for (const sub of targetSubs) {
      const lines = sub.subscriptionLines.map(mapListLineToApiLine);
      const pickSub = {
        subscriptionLines: lines,
        totalOrderCount: 0,
        billingPolicyInterval: sub.billingPolicyInterval,
        billingPolicyIntervalCount: sub.billingPolicyIntervalCount,
      };
      const bss = satisfiedBoxSettings(lines, categoryIds, boxSettings);
      const ctx = createCategoryContext(lines, 0, customerTags ?? [], totalSpent ?? '');

      try {
        const built = buildSubscriptionProducts(
          pickSub,
          products,
          planGroups,
          categories,
          bss,
          ctx
        );
        out[sub.subscriptionContractId] = built.subscribedProducts;
      } catch (e) {
        console.error('useSubscriptionListSubscribedDisplay', e);
        out[sub.subscriptionContractId] = [];
      }
    }
    return out;
  }, [
    targetSubs,
    categories,
    boxSettings,
    products,
    planGroups,
    customerTags,
    totalSpent,
  ]);

  const layoutReady =
    !!categories &&
    !!boxSettings &&
    products !== null &&
    hasFetchedPlanGroupsRef.current;

  return { subscribedByContractId, layoutReady };
}
